import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { checkPaymentStatus } from "../../../services/paymentServices";

const PaymentConfirmation = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(30);

  const token = queryParams.get("token");

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (token) {
        try {
          setLoading(true);
          const response = await checkPaymentStatus(token);
          setPaymentDetails(response.data);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [token]);

  // Countdown timer and redirect
  useEffect(() => {
    if (!loading && paymentDetails) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = "/";
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [loading, paymentDetails]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div
          className="card shadow-lg text-center p-4 align-items-center"
          style={{ maxWidth: "400px" }}
        >
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5>Checking Payment Status...</h5>
        </div>
      </div>
    );
  }

  const getPaymentStatus = () => {
    if (!paymentDetails) return "failed";

    // Check the main state and payment details
    const mainState = paymentDetails.state?.toLowerCase();
    const paymentState =
      paymentDetails.paymentDetails?.[0]?.state?.toLowerCase();

    if (mainState === "completed" || paymentState === "completed") {
      return "success";
    } else if (mainState === "failed" || paymentState === "failed") {
      return "failed";
    } else if (mainState === "pending" || paymentState === "pending") {
      return "pending";
    }

    return "failed";
  };

  const paymentStatus = getPaymentStatus();
  const formatAmount = (amount) => {
    return amount ? (amount / 100).toFixed(2) : "0.00";
  };

  return (
    <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
      <div
        className="card shadow-lg text-center p-4"
        style={{ maxWidth: "450px" }}
      >
        {paymentStatus === "success" ? (
          <>
            <div className="text-success mb-3">
              <i
                className="bi bi-check-circle-fill"
                style={{ fontSize: "4rem" }}
              ></i>
            </div>
            <h2 className="text-success">Payment Successful</h2>
            <p className="text-muted">
              Thank you! Your payment has been processed successfully.
            </p>
            <div className="alert alert-info p-2 mb-3">
              <small>
                Please wait while we are adding your subscription...
              </small>
            </div>
            {paymentDetails && (
              <div className="text-start mb-3">
                <small className="text-muted">
                  <strong>Order ID:</strong> {paymentDetails.orderId}
                  <br />
                  <strong>Amount:</strong> ₹
                  {formatAmount(paymentDetails.amount)}
                  <br />
                  {paymentDetails.paymentDetails?.[0]?.transactionId && (
                    <>
                      <strong>Transaction ID:</strong>{" "}
                      {paymentDetails.paymentDetails[0].transactionId}
                      <br />
                    </>
                  )}
                  <strong>Status:</strong> {paymentDetails.state}
                </small>
              </div>
            )}
            <p className="text-muted small">
              Redirecting to dashboard in <strong>{countdown}</strong>{" "}
              seconds...
            </p>
            <button
              className="btn btn-success w-100"
              onClick={() => (window.location.href = "/")}
            >
              Go to Dashboard Now
            </button>
          </>
        ) : paymentStatus === "pending" ? (
          <>
            <div className="text-warning mb-3">
              <i className="bi bi-clock-fill" style={{ fontSize: "4rem" }}></i>
            </div>
            <h2 className="text-warning">Payment Pending</h2>
            <p className="text-muted">
              Your payment is being processed. Please wait...
            </p>
            {paymentDetails && (
              <div className="text-start mb-3">
                <small className="text-muted">
                  <strong>Order ID:</strong> {paymentDetails.orderId}
                  <br />
                  <strong>Amount:</strong> ₹
                  {formatAmount(paymentDetails.amount)}
                  <br />
                  <strong>Status:</strong> {paymentDetails.state}
                </small>
              </div>
            )}
            <p className="text-muted small">
              Redirecting to dashboard in <strong>{countdown}</strong>{" "}
              seconds...
            </p>
            <button
              className="btn btn-warning w-100"
              onClick={() => (window.location.href = "/")}
            >
              Go to Dashboard
            </button>
          </>
        ) : (
          <>
            <div className="text-danger mb-3">
              <i
                className="bi bi-x-circle-fill"
                style={{ fontSize: "4rem" }}
              ></i>
            </div>
            <h2 className="text-danger">Payment Failed</h2>
            <p className="text-muted">
              Oops! Something went wrong with your transaction.
            </p>
            {paymentDetails && (
              <div className="text-start mb-3">
                <small className="text-muted">
                  <strong>Order ID:</strong> {paymentDetails.orderId}
                  <br />
                  {paymentDetails.amount && (
                    <>
                      <strong>Amount:</strong> ₹
                      {formatAmount(paymentDetails.amount)}
                      <br />
                    </>
                  )}
                  <strong>Status:</strong> {paymentDetails.state}
                </small>
              </div>
            )}
            {error && (
              <div className="alert alert-danger p-2 mb-3">
                <small>{error}</small>
              </div>
            )}
            <p className="text-muted small">
              Redirecting to dashboard in <strong>{countdown}</strong>{" "}
              seconds...
            </p>
            <button
              className="btn btn-outline-secondary w-100"
              onClick={() => (window.location.href = "/")}
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentConfirmation;
